/**

 * Composes today's guest plan from answers (not fixed canned paragraphs).

 * Returns structured slots + interpolation params for i18n templates.

 */



import { hasMedicalCaution, normalizeMedicalFlags } from './quickhook-medical.js';
import { pickPrimaryGoal, normalizeGoals } from './quickhook-goals.js';
import { pickPrimaryFeeling, normalizeFeelings } from './quickhook-feeling.js';

/**
 * @param {{
 *  goal?: string,
 *  goals?: string[],
 *  feeling?: string,
 *  feelings?: string[],
 *  age: number,
 *  medicalFlags?: string[]
 * }} input
 */
export function buildTodayPlanSpec(input) {
  const goals = normalizeGoals(input.goals || input.goal);
  const goal = pickPrimaryGoal(goals);
  const feelings = normalizeFeelings(input.feelings || input.feeling);
  const feeling = pickPrimaryFeeling(feelings);
  const age = Number(input.age) || 40;
  const medicalFlags = normalizeMedicalFlags(input.medicalFlags);
  const medicalHard = hasMedicalCaution(medicalFlags);
  const familyOnly =
    medicalFlags.includes('family_history') &&
    !medicalHard &&
    !medicalFlags.includes('none');

  let intensity = 'steady';
  if (feeling === 'tired' || feeling === 'stressed' || feeling === 'anxious' || feeling === 'sleepy') {
    intensity = 'gentle';
  }
  if (feeling === 'sore' || medicalHard) intensity = 'protective';
  if ((feeling === 'good' || feeling === 'motivated' || feeling === 'energetic') && !medicalHard && age < 60) {
    intensity = 'steady';
  }
  if ((feeling === 'good' || feeling === 'okay') && !medicalHard && age >= 60) intensity = 'measured';
  if (feeling === 'energetic' && !medicalHard && age < 50 && !goals.includes('move_better')) {
    intensity = 'steady';
  }


  let minutesLow = 22;

  let minutesHigh = 38;

  if (intensity === 'gentle') {

    minutesLow = 12;

    minutesHigh = 22;

  } else if (intensity === 'protective') {

    minutesLow = 8;

    minutesHigh = 15;

  } else if (intensity === 'measured') {

    minutesLow = 15;

    minutesHigh = 28;

  }



  if (age >= 70 && intensity !== 'protective') {

    minutesLow = Math.max(8, minutesLow - 4);

    minutesHigh = Math.max(minutesLow + 5, minutesHigh - 6);

  }



  const modalityByGoal = {
    lose_weight: 'walk_or_light_strength',
    gain_muscle: 'light_strength',
    eat_better: 'walk_or_light_strength',
    sleep_better: 'calm_walk',
    longevity: 'mobility_walk',
    mental_wellbeing: 'calm_walk',
    more_energy: 'short_activation',
    move_better: 'mobility_technique',
  };

  let modality = modalityByGoal[goal] || 'mobility_walk';
  // Secondary goals nudge modality when primary alone would look identical for many users.
  if (goals.includes('move_better') && goal !== 'move_better' && intensity !== 'protective') {
    modality = 'mobility_technique';
  } else if (goals.includes('sleep_better') && goal !== 'sleep_better') {
    modality = 'calm_walk';
  } else if (goals.includes('more_energy') && goal === 'longevity') {
    modality = 'short_activation';
  }
  if (intensity === 'protective') modality = 'mobility_only';
  else if (feeling === 'sore') modality = 'mobility_only';
  else if (feeling === 'stressed' || feeling === 'anxious') modality = 'calm_walk';
  else if (feeling === 'tired' && modality === 'light_strength') modality = 'walk_or_light_strength';
  else if (feeling === 'sleepy') modality = 'calm_walk';

  const nutritionByGoal = {
    lose_weight: 'protein_cut_sugars',
    gain_muscle: 'surplus_protein',
    eat_better: 'balanced_fiber',
    sleep_better: 'regular_meals',
    longevity: 'balanced_fiber',
    mental_wellbeing: 'regular_meals',
    more_energy: 'protein_breakfast',
    move_better: 'simple_plates',
  };

  let nutrition = nutritionByGoal[goal] || 'balanced_fiber';
  if (goals.includes('eat_better') && goal !== 'lose_weight' && goal !== 'gain_muscle') {
    nutrition = 'balanced_fiber';
  } else if (goals.includes('more_energy') && nutrition === 'balanced_fiber') {
    nutrition = 'protein_breakfast';
  }

  const recoveryByFeeling = {
    energetic: 'breathe_hydrate',
    motivated: 'breathe_hydrate',
    good: 'breathe_hydrate',
    okay: 'breathe_hydrate',
    sleepy: 'early_rest',
    tired: 'early_rest',
    sore: 'heat_sleep',
    stressed: 'slow_breath_screens',
    anxious: 'slow_breath_screens',
  };



  let recovery = recoveryByFeeling[feeling] || 'breathe_hydrate';

  if (medicalHard) recovery = 'medical_respect';

  else if (familyOnly) recovery = 'family_aware';



  return {

    intensity,

    modality,

    nutrition,

    recovery,

    minutes: `${minutesLow}–${minutesHigh}`,

    medicalFlags,

    medicalHard,

    familyOnly,

    goal,

    feeling,

    age,

  };

}



/**

 * Build localized bullet strings via next-intl translator.

 * @param {Function} t - useTranslations('QuickHook') bound t

 * @param {ReturnType<typeof buildTodayPlanSpec>} spec

 * @returns {string[]}

 */

export function formatTodayPlanBullets(t, spec) {

  const bullets = [

    t('result.compose.training', {

      intensity: t(`result.compose.intensity.${spec.intensity}`),

      modality: t(`result.compose.modality.${spec.modality}`),

      minutes: spec.minutes,

    }),

    t('result.compose.nutrition', {

      focus: t(`result.compose.nutritionFocus.${spec.nutrition}`),

    }),

    t('result.compose.recovery', {

      focus: t(`result.compose.recoveryFocus.${spec.recovery}`),

    }),

  ];



  if (spec.medicalHard) {

    bullets.push(t('result.compose.medicalHardNote'));

  } else if (spec.familyOnly) {

    bullets.push(t('result.compose.familyNote'));

  }



  return bullets;

}


